// cleanup_convert_to_glu_component_SMBPYMT
// cleanup_convert_to_typescript_file_SMBPYMT
// cleanup_remove_external_libraries_SMBPYMT

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { template, uniqueId } from 'lodash';
import PropTypes from 'prop-types';

import {
  simplifiedPaymentsApprove,
  simplifiedPaymentsReject,
  simplifiedPaymentsDelete
} from 'api/smallBusinessPayments/service';
import ModalNotification from 'components/modalNotification/ModalNotification';
import BasicMobileGrid from 'components/basicMobileGrid/BasicMobileGrid';
import {
  transformColumns,
  buildColumnState
} from 'components/basicMobileGrid/BasicMobileGrid.utils';
import ChallengeManager from 'components/challengeManager/ChallengeManager';
import useLocale from 'components/useLocale';
import { PAYMENTS_COLUMNS } from 'components/widgets/payments/web/payments.constants';
import {
  PAYMENT_ACTIONS,
  DATE_FORMAT,
  WARNING_CODES,
  API_ERROR,
  CURRENCY,
  ACTIONS,
  GLU_MODAL_TYPES
} from 'constants.js';
import { setPaymentActionMessages as setReduxMessage } from 'logic/store/payments/actions';
import { numberToCurrency } from 'utils/currency';

import makeColumns from '../smallBusinessPayments.columns';
import { staticViews } from '../smallBusinessPayments.staticViews';
import NativeActionConfirmation from './NativeActionConfirmation';
import useStyles from './SmallBusinessPayments.styles';

const { ACCOUNT_LOCKED } = API_ERROR;
const DATE = 'Date';
const { ERROR } = GLU_MODAL_TYPES;
const { APPROVE, DELETE, REJECT } = PAYMENT_ACTIONS;
const {
  amount,
  bankTrace,
  comments,
  confirmationInfo,
  custTrace,
  date,
  fromAccountName,
  fromAccountNumber,
  payeeName,
  paymentType,
  possibleActions,
  selector,
  wirePaymentStatus
} = PAYMENTS_COLUMNS;
const { DUPLICATE_PAYMENT_EXISTS } = WARNING_CODES;

const getDuplicateDetails = (data) =>
  data.map((row) =>
    Object.keys(row).map((key) => ({
      head: key,
      value: key.includes(DATE) && row[key] ? moment(row[key]).format(DATE_FORMAT) : row[key]
    }))
  );

const createMessage = ({ action, data, locale, title }) => {
  const message = data.payment || {};
  const actionSuccess = action === DELETE ? data.success : message.actionSuccess;
  let duplicateWarning = {};
  if (message.warning) {
    const warningResponse = message.warningResponse.filter(
      ({ warningCode }) => warningCode === DUPLICATE_PAYMENT_EXISTS
    );
    if (warningResponse.length) {
      duplicateWarning = {
        duplicateMsg: warningResponse[0].warningMessage,
        duplicateDetails: getDuplicateDetails(message.duplicateAchPayment)
      };
    }
  }
  return [
    {
      success: actionSuccess && !duplicateWarning.duplicateMsg,
      title: actionSuccess ? title.success : title.failure,
      id: uniqueId(),
      records: [data.payment],
      ...duplicateWarning,
      details: [
        ...(message.payeeName
          ? [{ head: locale['common.payeeName'], value: message.payeeName }]
          : []),
        ...(message.transactionDate
          ? [
              {
                head: locale['common.transactionDate'],
                value: moment.parseZone(message.transactionDate).format(DATE_FORMAT)
              }
            ]
          : []),
        ...(message.amount
          ? [
              {
                head: locale['common.amount'],
                value: numberToCurrency(message.amount)
              }
            ]
          : []),
        ...(message.paymentType
          ? [{ head: locale['common.paymentType'], value: message.paymentType }]
          : []),

        ...(message.reason ? [{ head: locale['common.reason'], value: message.reason }] : []),
        ...(!actionSuccess
          ? message?.actionMessages?.reduce(
              (acc, msg) => {
                return [{ ...acc[0], value: `${acc[0].value} ${msg.userMessage}` }];
              },
              [{ head: locale['common.error'], value: '' }]
            )
          : [])
      ]
    }
  ];
};

export const SmallBusinessPaymentsNativeComponent = ({ refresh, smbPaymentsData }) => {
  const [challengeManager, setChallengeManager] = useState(false);
  const [challengeErrors, setChallengeErrors] = useState(false);
  const [confirmation, setConfirmation] = useState(false);
  const dispatch = useDispatch();
  const { challengeManagerContainer, modalNotificationContainer, nativeModal } = useStyles();
  const locale = useLocale();
  const columns = makeColumns({ locale, records: smbPaymentsData });
  const primaryFieldNames = [payeeName, amount, wirePaymentStatus];
  const defaultFieldNames = [
    fromAccountName,
    fromAccountNumber,
    date,
    paymentType,
    comments,
    bankTrace,
    custTrace,
    confirmationInfo
  ];
  const primaryFields = primaryFieldNames.map((field) =>
    columns.find((col) => field === col.field)
  );
  const defaultFields = columns.filter(({ field }) => defaultFieldNames.includes(field));
  const webColumns = [...primaryFields, ...defaultFields];

  const sbColumns = transformColumns({
    columns: webColumns,
    smbPaymentsData,
    primary: primaryFieldNames
  });

  const columnState = buildColumnState(primaryFieldNames);

  const getPayload = (data) => {
    return {
      custTrace: data?.custTrace || null,
      bankTrace: data?.bankTrace || null,
      paymentType: data?.paymentType || null,
      status: data?.status || null,
      payeeName: data?.payeeName || null,
      transactionDt: data?.date || null,
      amount: data?.debitAmount || null
    };
  };

  const afterAction = (data, action) => {
    if (!data) return;
    let successTitle = '';
    let failureTitle = '';
    if (action === APPROVE) {
      successTitle = template(locale['smallBusinessPayments.successApproveTitle'])({ count: '' });
      failureTitle = template(locale['smallBusinessPayments.failApproveTitle'])({ count: '' });
    }
    if (action === REJECT) {
      successTitle = template(locale['smallBusinessPayments.successRejectTitle'])({ count: '' });
      failureTitle = template(locale['smallBusinessPayments.failRejectTitle'])({ count: '' });
    }
    if (action === DELETE) {
      successTitle = template(locale['smallBusinessPayments.successDeleteTitle'])({ count: '' });
      failureTitle = template(locale['smallBusinessPayments.failDeleteTitle'])({ count: '' });
    }
    const alertMessage = createMessage({
      data,
      locale,
      title: {
        success: successTitle,
        failure: failureTitle
      },
      action
    });
    dispatch(setReduxMessage(alertMessage));
    refresh();
  };

  const handleAction = (action, rowData, bypassConfirm = false) => {
    const endpointMap = {
      [APPROVE]: simplifiedPaymentsApprove,
      [REJECT]: simplifiedPaymentsReject,
      [DELETE]: simplifiedPaymentsDelete
    };
    const confirmModalContentMap = {
      [APPROVE]: {
        header: locale['common.approvePayment'],
        title: locale['smallBusinessPayments.paymentToBeApproved'],
        action: locale['common.approve'],
        presentTenseAction: locale['common.approvingAction']
      },
      [REJECT]: {
        header: locale['common.rejectPayment'],
        title: locale['smallBusinessPayments.paymentToBeRejected'],
        action: locale['common.reject'],
        presentTenseAction: locale['common.rejectingAction']
      },
      [DELETE]: {
        header: locale['common.deletePayment'],
        title: locale['smallBusinessPayments.paymentToBeDeleted'],
        action: locale['common.delete'],
        presentTenseAction: locale['common.deletingAction']
      }
    };
    const payment = getPayload(rowData);
    const endPoint = endpointMap[action];
    const execute = () =>
      endPoint({
        ids: [payment]
      }).subscribe((response) => {
        let responseData = response;
        if (Array.isArray(response)) {
          responseData = response[0];
        }
        // Handle challenge manager
        if (responseData?.challeangeable === true || responseData?.[0]?.code === ACCOUNT_LOCKED) {
          setChallengeErrors(Boolean(responseData.numberOfFailedAttempts));
          return setChallengeManager({
            ...responseData,
            callback: () => handleAction(action, rowData, true)
          });
        }
        setChallengeManager(false);
        setChallengeErrors(false);

        return afterAction(responseData, action);
      });
    if (bypassConfirm) {
      return execute();
    }
    return setConfirmation({
      component: () => (
        <NativeActionConfirmation
          execute={execute}
          words={confirmModalContentMap[action]}
          setConfirmation={setConfirmation}
        />
      )
    });
  };
  const staticViewsObject = staticViews({ locale, columnDefs: columns });
  return (
    <div data-qa="small-business-payments-mobile">
      {challengeManager && (
        <div className={challengeManagerContainer}>
          <ChallengeManager
            challengeErrorMessage={challengeErrors}
            onCancel={() => {
              setChallengeManager(false);
              setConfirmation(null);
            }}
            onHide={() => {
              setChallengeManager(false);
              setConfirmation(null);
            }}
            data={challengeManager}
            onSubmit={() => challengeManager.callback()}
            open
          />
        </div>
      )}
      {confirmation && (
        <div className={modalNotificationContainer}>
          <ModalNotification
            variant={ERROR.toLowerCase()}
            onHide={() => setConfirmation(null)}
            suppressIcon={false}
            data-qa="action-modal"
            show={confirmation}
            dialogClassName={nativeModal}
          >
            <confirmation.component />
          </ModalNotification>
        </div>
      )}
      <BasicMobileGrid
        gridId="small-business-payments"
        rowColumnId="field"
        columnDefs={sbColumns.map((col) => {
          if (col.colId === amount) {
            return { ...col, variant: CURRENCY };
          }
          return col;
        })}
        columnState={columnState}
        records={smbPaymentsData}
        localeText={{ noRowsToShow: locale['common.grid.noRecordsFound'] }}
        savedViews={{
          ...staticViewsObject,
          views: staticViewsObject?.views?.map((view) => {
            return {
              ...view,
              primary: [payeeName, amount, wirePaymentStatus]
            };
          })
        }}
        formatters={{
          valueFallback: locale['common.grid.empty'],
          fields: [
            {
              field: amount,
              format: ({ data }) =>
                data.amount ? numberToCurrency(data.amount) : locale['common.grid.empty']
            }
          ]
        }}
        exclude={[selector, possibleActions]}
        getRowActions={[
          {
            id: APPROVE,
            label: locale['common.approve'],
            onClick: ({ rowData }) => handleAction(APPROVE, rowData),
            condition: ({ actions }) => actions.includes(ACTIONS.APPROVE)
          },
          {
            id: REJECT,
            label: locale['common.reject'],
            onClick: ({ rowData }) => handleAction(REJECT, rowData),
            condition: ({ actions }) => actions.includes(ACTIONS.REJECT)
          },
          {
            id: DELETE,
            label: locale['common.delete'],
            onClick: ({ rowData }) => handleAction(DELETE, rowData),
            condition: ({ actions }) => actions.includes(ACTIONS.DELETE)
          }
        ]}
      />
    </div>
  );
};
SmallBusinessPaymentsNativeComponent.propTypes = {
  smbPaymentsData: PropTypes.shape([]).isRequired,
  refresh: PropTypes.func.isRequired
};

export default SmallBusinessPaymentsNativeComponent;
